import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardListIcon, CopyIcon, ExternalLinkIcon, LinkIcon, PencilIcon, PlusIcon, PowerIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createForm, deleteForm, duplicateForm, setFormStatus } from '../../services/formService';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Tag } from '../../components/ui/Tag';
export function Forms() {
  const navigate = useNavigate();
  const {
    organizationId,
    forms,
    leads,
    landings
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    const form = await createForm(organizationId, newName.trim());
    setSaving(false);
    setCreating(false);
    setNewName('');
    navigate(`/app/formularios/${form.id}`);
  }
  return <>
      <PageHeader title="Formularios" description="Crea las preguntas que verán tus potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-863" data-mp="1rveh8jyh8j2a" />} onClick={() => setCreating(true)} data-id="element-862" data-mp="1rvnzflda5lxf">
            Crear formulario
          </Button>} data-id="element-861" data-mp="1wn60saj7toix" />

      {forms.length === 0 ? <EmptyState icon={<ClipboardListIcon className="h-5 w-5" data-id="element-865" data-mp="2kdijmgg015oa" />} title="Todavía no tienes formularios" description="Crea tu primer formulario y asócialo a una landing para empezar a recibir leads." action={<Button onClick={() => setCreating(true)} data-id="element-866" data-mp="2yisjvt7ppjg8">Crear formulario</Button>} data-id="element-864" data-mp="2kd0f6wj6nl5a" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-867" data-mp="2yklrpbu2a9pt">
          {forms.map(form => {
        const responses = leads.filter(l => l.origin.formId === form.id).length;
        const linked = landings.filter(l => l.formId === form.id);
        return <li key={form.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-868" data-mp="2tr0qzc1331mx">
                <div className="flex items-start justify-between gap-3" data-id="element-869" data-mp="2fr9fbhy1hxc3">
                  <div className="min-w-0" data-id="element-870" data-mp="2kre0hl9w7g8n">
                    <Link to={`/app/formularios/${form.id}`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-871" data-mp="2ksf9zlb9fuo9">
                      {form.name}
                    </Link>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground" data-id="element-872" data-mp="2av0z0183vy3c">
                      {form.description || 'Sin descripción'}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-874" data-mp="1tjcenumq0b2w">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-875" data-mp="239ypzcnemffs" />,
              onSelect: () => navigate(`/app/formularios/${form.id}`)
            }, {
              label: 'Vista previa',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-876" data-mp="2d7l7m6w38l3g" />,
              onSelect: () => window.open(`/formulario/${form.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-877" data-mp="28auw7wv8adod" />,
              onSelect: () => navigator.clipboard?.writeText(`${window.location.origin}/formulario/${form.slug}`)
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-878" data-mp="ru094mlzip40" />,
              onSelect: () => duplicateForm(form.id)
            }, {
              label: form.status === 'activo' ? 'Desactivar' : 'Activar',
              icon: <PowerIcon className="h-4 w-4" data-id="element-879" data-mp="pa3yabdpplxd" />,
              onSelect: () => setFormStatus(form.id, form.status === 'activo' ? 'inactivo' : 'activo')
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-880" data-mp="ya0io0t2jymm" />,
              destructive: true,
              onSelect: () => setToDelete(form.id)
            }]} data-id="element-873" data-mp="8zh2iguk7a5g" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-881" data-mp="5luwraqa43l7">
                  <div data-id="element-882" data-mp="5no5qwod6zwd">
                    <dt className="text-xs text-muted-foreground" data-id="element-883" data-mp="5k0czzqquhip">Preguntas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-884" data-mp="al7lk10pnkqf">
                      {form.questions.length}
                    </dd>
                  </div>
                  <div data-id="element-885" data-mp="kbspm3z5lhgq">
                    <dt className="text-xs text-muted-foreground" data-id="element-886" data-mp="kcbvgmbj4fee">Respuestas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-887" data-mp="3izpsu0ituw8z">{responses}</dd>
                  </div>
                  <div data-id="element-888" data-mp="3nyj9g1tev3dd">
                    <dt className="text-xs text-muted-foreground" data-id="element-889" data-mp="3nxrdgpvwj3mk">Creado</dt>
                    <dd className="text-muted-foreground" data-id="element-890" data-mp="3gf06kb0nifdv">
                      {formatDate(form.createdAt)}
                    </dd>
                  </div>
                  <div data-id="element-891" data-mp="36hvnik23r01a">
                    <dt className="text-xs text-muted-foreground" data-id="element-892" data-mp="36h41o0yb4go7">Modificado</dt>
                    <dd className="text-muted-foreground" data-id="element-893" data-mp="31jkbc2oc4dfw">
                      {formatDate(form.updatedAt)}
                    </dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4" data-id="element-894" data-mp="2sqqtbxhi6zyc">
                  <Tag data-id="element-895" data-mp="2nrnooe5fqvqw">
                    {form.status === 'activo' ? 'Activo' : form.status === 'borrador' ? 'Borrador' : 'Inactivo'}
                  </Tag>
                  {linked.map(landing => <Tag key={landing.id} data-id="element-896" data-mp="2iqq6w2oc83px">{landing.name}</Tag>)}
                  {linked.length === 0 && <span className="text-xs text-muted-foreground" data-id="element-897" data-mp="2dvkkz3uxqp1u">
                      Sin landing asociada
                    </span>}
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nuevo formulario" footer={<div className="flex justify-end gap-2" data-id="element-899" data-mp="247f7t3j6vpoc">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-900" data-mp="1q6mdaisw0ul8">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!newName.trim()} data-id="element-901" data-mp="1v4e8blotwwtn">
              Crear
            </Button>
          </div>} data-id="element-898" data-mp="1z5g9i82b3iy9">
        <Input id="nombre-form" label="Nombre del formulario" placeholder="Solicitud de presupuesto" value={newName} onChange={e => setNewName(e.target.value)} data-id="element-902" data-mp="2iwuy6iet5t3i" />
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar formulario" description="Las landings asociadas se quedarán sin formulario. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteForm(toDelete);
      setToDelete(null);
    }} data-id="element-903" data-mp="294fi5qn2nel9" />
    </>;
}